import styled, { css, keyframes } from 'styled-components'

import { Icon } from '@/components/ui/icon'
import { media, thinScrollbar } from '@/theme/common'

import { Button } from '../button'
import { CHECKBOX_BOX_SIZE_MAP } from '../checkbox/checkbox.constants'
import { Tooltip } from '../tooltip'
import { CHECKBOX_COLUMN_WIDTH } from './table.constants'

export const Container = styled.div<{ $fillHeight?: boolean }>(({ $fillHeight }) => ({
  display: 'flex',
  flex: '1 min-content',
  flexDirection: 'column',
  maxHeight: '100%',
  overflow: 'hidden',
  position: 'relative',
  width: '100%',
  ...($fillHeight && { minHeight: '40rem' }),
}))

export const Wrapper = styled.div<{ $hasVerticalScroll?: boolean }>(
  ({ theme, $hasVerticalScroll }) => css`
    ${thinScrollbar};
    display: flex;
    flex-direction: column;
    flex: 1;
    border: solid ${theme.border.size['1']} ${theme.colors.gray400};
    border-radius: ${theme.border.radius['6']};
    overflow: auto;
    /* Note: only reserve scrollbar-gutter space once the table actually has vertical
       overflow ($hasVerticalScroll, computed via ResizeObserver in table.tsx) - reserving it
       unconditionally left a permanent empty strip on the right of short/empty tables that
       never scroll. */
    scrollbar-gutter: ${$hasVerticalScroll ? 'stable' : 'auto'};
  `,
)

export const Table = styled.table<{
  $hasVerticalScroll?: boolean
  $hasHorizontalScroll?: boolean
  $fillHeight?: boolean
}>(
  ({ theme, $hasVerticalScroll, $hasHorizontalScroll, $fillHeight }) => css`
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
    width: 100%;
    height: ${$fillHeight ? '100%' : undefined};

    thead {
      position: sticky;
      top: 0;
      z-index: ${theme.zIndex.level2};
      flex-shrink: 0;
      background-color: ${theme.colors.gray100};

      th {
        position: relative;
        font-size: 1.2rem;
        font-weight: 600;
        line-height: 1.6rem;
        color: ${theme.colors.black600};
        // NOTE: You may see height of a 'thead' row as '39' px which is correct, due to table has border-top which is size['1']
        padding: calc(${theme.space['12']} - ${theme.border.size['1']}) ${theme.space['16']};
        border-bottom: solid ${theme.border.size['1']} ${theme.colors.gray400};
        background-color: ${theme.colors.gray100};

        // Note: horizontal-only reduction - mobile is scarce on width, not row height, and every
        // pixel here comes straight out of cell content on an already-cramped viewport.
        ${media.down('sm')} {
          padding: calc(${theme.space['12']} - ${theme.border.size['1']}) ${theme.space['8']};
        }

        @media (pointer: fine) {
          &:last-child {
            border-right: ${
              $hasVerticalScroll
                ? `solid ${theme.border.size['1']} ${theme.colors.gray400}`
                : undefined
            };
          }
        }
      }
    }

    /* Note: see TableColumn.highlighted - only a 'from' frame, so it fades back into whatever
    background the cell normally has (header gray, body white, hover). */
    th[data-highlighted='true'],
    td[data-highlighted='true'] {
      animation: ${keyframes`from { background-color: ${theme.colors.blue100}; }`} 1.6s ease-out;
    }

    tbody {
      td {
        font-size: 1.2rem;
        font-weight: 500;
        line-height: 1.6rem;
        text-overflow: ellipsis;
        vertical-align: middle;
        height: 4.8rem;
        color: ${theme.colors.black800};
        border-bottom: solid ${theme.border.size['1']} ${theme.colors.gray400};
        padding: ${theme.space['4']} ${theme.space['16']};
        background-color: ${theme.colors.white};

        ${media.down('sm')} {
          padding: ${theme.space['4']} ${theme.space['8']};
        }

        @media (pointer: fine) {
          &:last-child {
            border-right: ${
              $hasVerticalScroll
                ? `solid ${theme.border.size['1']} ${theme.colors.gray400}`
                : undefined
            };
          }
        }
      }

      @media (pointer: fine) {
        tr:last-child td {
          border-bottom: ${$hasHorizontalScroll || !$hasVerticalScroll ? undefined : 'none'};
        }
      }

      @media not (pointer: fine) {
        tr:last-child td {
          border-bottom: ${!$hasVerticalScroll ? undefined : 'none'};
        }
      }
    }
  `,
)

export const HeaderCellContent = styled.div<{ $actionsWidth?: string | null }>(
  ({ theme, $actionsWidth }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.space['4']};
    /* Note: lets the title below shrink/truncate instead of wrapping - a flex item's default
    min-width is 'auto' (its content's intrinsic width), which overrides white-space: nowrap on
    the title and forces the column to wrap onto a second line once it's narrower than that. */
    min-width: 0;

    /* Note: keeps the title clear of the absolutely positioned ColumnActionsWrapper. The column
       grows to fit the title plus this reserve when its own width is too narrow (getColumnWidth).
       On desktop the th's 16px padding minus the actions' 8px inset leaves an 8px gap; on mobile
       the th pads 8px, so the gap is added here. */
    ${
      $actionsWidth &&
      css`
      padding-right: ${$actionsWidth};

      ${media.down('sm')} {
        padding-right: calc(${$actionsWidth} + ${theme.space['4']});
      }
    `
    }
  `,
)

export const HeaderCellTitle = styled.span`
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

// Note: reuses ui/button's own `ghost` appearance (hover/focus-visible/active backgrounds, the
// standard `:focus-visible` outline) instead of reimplementing button states here - only the
// sizing is table-specific (a denser 24x24 box than Button's own `onlyBox` sizing) and the
// hide-until-relevant visibility (hidden until the row is hovered/focused, or the button's own
// `active` prop applies - e.g. the column is currently sorted, or its dropdown is open). Shared
// by both the sort toggle and the column-actions ("...") trigger - see table-head.tsx - since
// per the design they're identical buttons that only differ by icon.
export const HeaderActionButton = styled(Button)`
  display: none;

  && {
    width: ${({ theme }) => theme.space['24']};
    height: ${({ theme }) => theme.space['24']};
    min-width: ${({ theme }) => theme.space['24']};
    padding: 0;
  }

  /* Note: the design's header-button states (Table Header: Default / Hovered / Active) - no
     background at rest, gray400 on hover, gray400 + a 0.75px black800 ring while pressed or its
     menu is open. The ring is an inset shadow so it doesn't shift the 24x24 box. Overrides the
     ghost Button's own gray300 hover / primary200 active. */
  && {
    &:hover,
    &:focus-visible {
      background: ${({ theme }) => theme.colors.gray400};
    }

    &:active,
    &[data-active='true'] {
      background: ${({ theme }) => theme.colors.gray400};
      box-shadow: inset 0 0 0 0.075rem ${({ theme }) => theme.colors.black800};
    }
  }

  th:hover &,
  &:focus-visible,
  &[data-active='true'],
  /* Note: shown without the Active look - e.g. a sorted column's arrow or a pinned column's unpin
     button, which stay visible at rest per the design. */
  &[data-visible='true'],
  /* Note: the tooltip is portaled outside the th, so hovering it un-hovers the column. While any
     action's tooltip is open, keep the whole group shown - not just that button - or its siblings
     hide, the remaining buttons shift over and the tooltip is left pointing at the wrong spot. */
  [data-column-actions]:has([data-component-id='Tooltip'][data-open='true']) & {
    display: flex;
  }
`

export const ColumnActionsWrapper = styled.div(
  ({ theme }) => css`
    position: absolute;
    top: 50%;
    right: ${theme.space['8']};
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    gap: ${theme.space['4']};
    transition: background-color ${theme.transition.duration.sm} ease;
  `,
)

export const PinnedHeaderCell = styled.th<{ $width: string; $isBulkMode?: boolean }>(
  ({ theme, $width, $isBulkMode }) => css`
    && {
      position: sticky;
      left: ${$isBulkMode ? CHECKBOX_COLUMN_WIDTH : 0};
      z-index: ${theme.zIndex.level1};
      width: ${$width};
      min-width: ${$width};
      max-width: ${$width};
      border-right: solid ${theme.border.size['1']} ${theme.colors.gray400};
    }
  `,
)

export const PinnedBodyCell = styled.td<{ $width: string; $isBulkMode?: boolean }>(
  ({ theme, $width, $isBulkMode }) => css`
    position: sticky;
    left: ${$isBulkMode ? CHECKBOX_COLUMN_WIDTH : 0};
    z-index: ${theme.zIndex.level1};
    width: ${$width};
    min-width: ${$width};
    max-width: ${$width};
    border-right: solid ${theme.border.size['1']} ${theme.colors.gray400};
  `,
)

export const StateRow = styled.tr`
  display: flex;
  flex-direction: column;
  position: absolute;
  // NOTE: 4rem is the height of the header row
  inset: 4rem 0;
  height: calc(100% - 4rem);
  pointer-events: none;
`

export const StateCell = styled.td(
  ({ theme }) => css`
    display: grid;
    place-items: center;
    height: 100% !important;
    border: none !important;
    padding: 0 !important;
    margin: ${theme.space['16']} !important;
    pointer-events: none;
    overflow: hidden;

    *:not(div) {
      pointer-events: initial;
    }
  `,
)

export const ErrorWrapper = styled.div`
  max-width: 70rem;
`

// Note: Always sticky - in bulk mode the checkbox column is effectively "pinned" so that a
// separately pinned data column (see PinnedHeaderCell/PinnedBodyCell) can sit flush next to it
// without a gap opening up between them during horizontal scroll.
export const CheckboxHeaderCell = styled.th(
  ({ theme }) => css`
    && {
      position: sticky;
      left: 0;
      z-index: ${theme.zIndex.level1};
      width: ${CHECKBOX_COLUMN_WIDTH};
      min-width: ${CHECKBOX_COLUMN_WIDTH};
      max-width: ${CHECKBOX_COLUMN_WIDTH};
      text-align: center;
      vertical-align: middle;
      /* Note: no vertical padding - the title cells (11px + 16px text + 11px) set the 40px header
         row from the table design, and the 20px checkbox centers in it. With the old 12px padding
         this cell grew the row to 45px, and the top-aligned titles no longer lined up with it. */
      padding: 0 ${theme.space['8']};
      border-right: solid ${theme.border.size['1']} ${theme.colors.gray400};
    }
  `,
)

export const CheckboxBodyCell = styled.td(
  ({ theme }) => css`
    position: sticky;
    left: 0;
    z-index: ${theme.zIndex.level1};
    width: ${CHECKBOX_COLUMN_WIDTH};
    min-width: ${CHECKBOX_COLUMN_WIDTH};
    max-width: ${CHECKBOX_COLUMN_WIDTH};
    text-align: center;
    vertical-align: middle;
    border-right: solid ${theme.border.size['1']} ${theme.colors.gray400};
  `,
)

// Note: sized to the 20px (medium) checkbox the selection column holds, so swapping the
// header's select-all checkbox for its smaller loader doesn't shrink the header row.
export const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${CHECKBOX_BOX_SIZE_MAP.medium};
`

// Note: Bulk actions bar wrapper for positioning
export const BulkActionsWrapper = styled.div(
  ({ theme }) => css`
    position: absolute;
    bottom: ${theme.space['16']};
    left: 50%;
    transform: translateX(-50%);
    z-index: ${theme.zIndex.level2};
    pointer-events: none;
  `,
)

// Note: Bulk actions bar at the bottom of the table
export const BulkActionsBar = styled.div(
  ({ theme }) => css`
    display: inline-flex;
    align-items: center;
    gap: ${theme.space['8']};
    padding: ${theme.space['12']};
    background-color: ${theme.colors.black800};
    border-radius: ${theme.border.radius['8']};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    pointer-events: auto;
  `,
)

export const BulkClearButton = styled.button(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    width: 2.4rem;
    height: 2.4rem;
    background: transparent;
    border: none;
    color: ${theme.colors.white};
    cursor: pointer;
    transition: opacity ${theme.transition.duration.sm} ease;

    &:hover {
      opacity: 0.7;
    }
  `,
)

export const BulkActionTooltip = styled(Tooltip)`
  && {
    width: fit-content;
  }
`

export const BulkSelectionInfo = styled.span(
  ({ theme }) => css`
    color: ${theme.colors.white};
    font-size: 1.4rem;
    font-weight: 400;
    padding: 0 ${theme.space['4']};
  `,
)

export const BodyRow = styled.tr<{ $clickable?: boolean; $expanded?: boolean }>(
  ({ theme, $clickable, $expanded }) => css`
    cursor: ${$clickable ? 'pointer' : 'default'};

    ${
      $expanded &&
      css`
      /* Note: When row is expanded, remove its bottom border so it visually
         connects with the collapsable content row below. */
      background-color: ${theme.colors.gray100};

      > td {
        border-bottom: none !important;
        background-color: ${theme.colors.gray100} !important;
      }
    `
    }

    &:hover {
      background-color: ${theme.colors.gray200};

      > td {
        background-color: ${theme.colors.gray200} !important;
      }
    }
  `,
)

export const BodyCell = styled.td(
  () => css`
    position: relative;
  `,
)

export const CollapsableToggleWrapper = styled.div(
  ({ theme }) => css`
    position: absolute;
    top: 50%;
    right: ${theme.space['12']};
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
  `,
)

export const CaretIcon = styled(Icon)<{ $expanded: boolean }>(
  ({ theme, $expanded }) => css`
    transform: ${$expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
    transition: transform ${theme.transition.duration.sm} ease;
  `,
)

export const CollapsableContentRow = styled.tr(
  ({ theme }) => css`
    background-color: ${theme.colors.gray100};
  `,
)

export const CollapsableContentCell = styled.td<{ $isOpen?: boolean }>(
  ({ theme, $isOpen }) => css`
    && {
      /* Note: Override generic BodyTable & td styles */
      height: auto;
      padding: 0;
      font-size: 0;
      line-height: 0;
      /* Note: Only show bottom border when expanded; when collapsed avoid extra 1px height */
      border-bottom: ${
        $isOpen ? `solid ${theme.border.size['1']} ${theme.colors.gray400}` : 'none'
      };
      background-color: ${theme.colors.gray100} !important;
    }
  `,
)

export const CollapsableContentInner = styled.div(
  ({ theme }) => css`
    overflow: hidden;
    max-height: 0;
    will-change: max-height;
    transition: max-height ${theme.transition.duration.md} ease;
  `,
)

// Note: Sticky cell for row actions - always visible on the right during the horizontal scroll
export const RowActionsCell = styled.td`
  position: sticky;
  right: 0;
  width: 0;
  min-width: 0;
  max-width: 0;
  padding: 0;
  border-bottom: none;
  background-color: transparent;
`

export const RowActionsWrapper = styled.div(
  ({ theme }) => css`
    position: absolute;
    top: 50%;
    right: ${theme.space['12']};
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color ${theme.transition.duration.sm} ease;
  `,
)

export const RowActionMenuTrigger = styled(Button)<{ $isOpen?: boolean }>(
  ({ $isOpen }) => css`
    opacity: ${$isOpen ? 1 : 0};

    tr:hover & {
      opacity: 1;
    }
  `,
)
